import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data?.reset_link) setDevLink(data.reset_link);
    } catch (err) {
      logger.error("Forgot password failed:", err);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="px-6 md:px-12 lg:px-24 py-24 max-w-md mx-auto">
      <p className="label-eyebrow text-[#555]">— Account recovery</p>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-none font-light mt-4">
        Forgot password?
      </h1>

      {!sent ? (
        <>
          <p className="text-[#555] mt-6 text-sm leading-relaxed">
            Enter the email you used to sign up. If it matches an account, we'll send a one-time link
            to reset your password.
          </p>
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div>
              <label className="label-eyebrow text-[#555]">Email</label>
              <Input
                data-testid="forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
              />
            </div>
            <Button
              type="submit"
              data-testid="forgot-submit"
              disabled={submitting || !email}
              className="w-full rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white py-6 h-auto label-eyebrow"
            >
              {submitting ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        </>
      ) : (
        <div data-testid="forgot-sent-message" className="mt-10 border border-[#1E3A8A]/30 bg-[#F4EFE6]/40 p-6">
          <p className="label-eyebrow text-[#1E3A8A]">— Check your email</p>
          <p className="font-serif-display text-2xl mt-3 text-[#111]">
            <span className="italic">Reset link sent.</span>
          </p>
          <p className="text-sm text-[#333] mt-4 leading-relaxed">
            If an account exists for <span className="font-mono-aam text-[#1E3A8A]">{email}</span>,
            we've sent a reset link. The link expires in one hour.
          </p>
          {devLink && (
            <div className="mt-6 p-4 bg-white border border-[#E5E0D8]" data-testid="forgot-dev-link-block">
              <p className="label-eyebrow text-[#555]">— Dev/preview only</p>
              <a
                href={devLink}
                data-testid="forgot-dev-link"
                className="font-mono-aam text-xs text-[#1E3A8A] break-all underline mt-2 block"
              >
                {devLink}
              </a>
            </div>
          )}
        </div>
      )}

      <p className="mt-8 text-sm text-[#555]">
        Remembered it?{" "}
        <Link to="/login" className="text-[#111] underline hover:text-[#1E3A8A]">
          Sign in
        </Link>
      </p>
    </div>
  );
}
